import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import { TRAFFIC_TYPES, useTrafficVisitTrend } from '@/api/common';
import { usePageFilterContext } from '@/components/page';
import { NotesPanel } from '@/components/panels';
import VisitBreakdownTrendChart from '@/features/charts/VisitBreakdownTrendChart';
import { StackModeToggleButton } from '@/features/StackModeToggleButton';
import { ROUTES } from '@/router';

type Props = {
  campaignIds?: string[];
  isLoading?: boolean;
};

const CampaignTotalVisitsChart = ({ campaignIds, isLoading: isCampaignLoading }: Props) => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();
  const [visitsMode, setVisitsMode] = useState<'stack' | 'percent'>('stack');

  const {
    trafficTrendData: data,
    isLoading: isChartDataLoading,
    error,
  } = useTrafficVisitTrend(startDate, endDate, {
    campaignIds,
    audienceId: audience?.id,
  });

  const isLoading = isChartDataLoading || isCampaignLoading;

  return (
    <NotesPanel
      startDate={startDate}
      endDate={endDate}
      title={<Trans>Total Visits</Trans>}
      noPadding
      actions={
        <StackModeToggleButton
          mode={visitsMode}
          size="large"
          tooltipPlacement="topRight"
          onChange={setVisitsMode}
        />
      }
      style={{ height: 579 }}
    >
      <VisitBreakdownTrendChart
        mode={visitsMode}
        data={data}
        isLoading={isLoading}
        error={error}
        tooltipRowLink={(item) => {
          const seriesId = item?.series?.userOptions?.id;
          if (seriesId === TRAFFIC_TYPES.TARGET) {
            return ROUTES.webTrafficActivityWithParams({
              'w.campaignIds': campaignIds,
              'w.audienceIds': audience?.isTAM ? undefined : audience?.id,
              'w.inTam': audience?.isTAM ? 'true' : undefined,
              'w.metrics.visits': '[1,]',
            });
          } else if (seriesId === TRAFFIC_TYPES.OTHER) {
            return ROUTES.webTrafficActivityWithParams({
              'w.campaignIds': campaignIds,
              'w.audienceIds!': audience?.isTAM ? undefined : audience?.id,
              'w.inTam': audience?.isTAM ? 'false' : undefined,
              'w.metrics.visits': '[1,]',
            });
          } else if (item?.key === 'total') {
            return ROUTES.webTrafficActivityWithParams({
              'w.campaignIds': campaignIds,
              'w.metrics.visits': '[1,]',
            });
          }
        }}
      />
    </NotesPanel>
  );
};

export default CampaignTotalVisitsChart;
